import { useState } from 'react';
import { CheckCircle2, Circle, Code2, Loader2, Trash2, XCircle } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { Check, CheckResult, PrintedTarget, describeAssertion, describeTarget, isPrintedCheck } from '@/reports/bundles/expectations';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { IconButton } from '../studioChrome';

/**
 * Writing a check by hand: what it is about — a table's cell, a labelled
 * value, a section, some text, or one of the report's findings — and what it
 * must be. A check picked from the preview starts here with its target filled.
 */

type About = 'cell' | 'field' | 'section' | 'text' | 'finding';
type Must = 'present' | 'reads' | 'contains' | 'between' | 'absent';
const ABOUTS: About[] = ['cell', 'field', 'section', 'text', 'finding'];
const MUSTS: Must[] = ['reads', 'contains', 'between', 'present', 'absent'];

const aboutOf = (target: PrintedTarget | undefined): About => (!target ? 'cell' : 'table' in target ? 'cell' : 'field' in target ? 'field' : 'section' in target ? 'section' : 'text');
/**
 * A bound as typed, read as the printed number is: commas grouping thousands
 * ("1,234" is 1234), or one decimal comma ("4,5" is 4.5). Null for none;
 * undefined for what is no number, which is not taken for "no bound".
 */
const parseBound = (s: string): number | null | undefined => {
  const typed = s.trim();
  if (typed === '') return null;
  const plain = /^[-−]?\d{1,3}(,\d{3})+(\.\d+)?$/.test(typed) ? typed.replace(/,/g, '') : typed.replace(',', '.');
  const n = Number(plain.replace('−', '-'));
  return Number.isFinite(n) ? n : undefined;
};

const SELECT = 'h-8 rounded-lg border-[1px] border-input bg-background px-2 text-sm';

export function CheckEditor({ initial, found, findings, onAdd, onCancel, addLabel, disabled }: {
  /** a target picked from the preview */
  initial?: PrintedTarget;
  /** what the preview prints there */
  found?: string[];
  /** the findings the fixture comes to, to pick from */
  findings?: Array<{ id: string; message: string; passed: boolean }> | null;
  onAdd: (check: Check) => void;
  onCancel: () => void;
  addLabel?: string;
  /** where it would be added cannot take it now */
  disabled?: boolean;
}) {
  const [about, setAbout] = useState<About>(aboutOf(initial));
  const [table, setTable] = useState(initial && 'table' in initial ? String(initial.table) : '');
  const [row, setRow] = useState(initial && 'table' in initial ? initial.row : '');
  const [column, setColumn] = useState(initial && 'table' in initial ? initial.column : '');
  const [label, setLabel] = useState(initial && 'field' in initial ? initial.field : initial && 'section' in initial ? initial.section : initial && 'text' in initial ? initial.text : '');
  // picked where nothing prints (a field left blank): what it is now is that it is not printed
  const [must, setMust] = useState<Must>(initial && 'text' in initial ? 'present' : initial && 'section' in initial ? 'present' : found?.length === 1 ? 'reads' : found && initial && found.length === 0 ? 'absent' : 'present');
  const [value, setValue] = useState(found?.[0] ?? '');
  const [low, setLow] = useState('');
  const [high, setHigh] = useState('');
  const [finding, setFinding] = useState(findings?.[0]?.id ?? '');
  const [passes, setPasses] = useState(findings?.[0]?.passed ?? true);

  const target = (): PrintedTarget | null => {
    if (about === 'cell') return table.trim() && row.trim() && column.trim() ? { table: /^\d+$/.test(table.trim()) ? Number(table.trim()) : table.trim(), row: row.trim(), column: column.trim() } : null;
    if (!label.trim()) return null;
    return about === 'field' ? { field: label.trim() } : about === 'section' ? { section: label.trim() } : { text: label.trim() };
  };
  const build = (): Check | null => {
    if (about === 'finding') return finding.trim() ? { finding: finding.trim(), passes } : null;
    const printed = target();
    if (!printed) return null;
    if (must === 'absent') return { printed, present: false };
    if (must === 'present') return { printed };
    if (must === 'between') {
      const [lowest, highest] = [parseBound(low), parseBound(high)];
      if (lowest === undefined || highest === undefined || (lowest === null && highest === null)) return null;
      return { printed, between: [lowest, highest] };
    }
    if (!value.trim()) return null;
    return must === 'reads' ? { printed, reads: value.trim() } : { printed, contains: value.trim() };
  };
  const check = build();

  return (
    <div className="flex flex-col gap-2 rounded-lg border-[1px] bg-muted/30 p-[0.75rem] text-sm">
      <label className="grid gap-1">
        <span className="text-xs text-muted-foreground">{I18n.get('checkAbout')}</span>
        <select aria-label={I18n.get('checkAbout')} value={about} onChange={(e) => {
          const next = e.target.value as About;
          setAbout(next);
          // text is found as itself: it is printed or not, nothing else
          if (next === 'text' && must !== 'present' && must !== 'absent') setMust('present');
        }} className={SELECT}>
          {ABOUTS.map((a) => <option key={a} value={a}>{I18n.get(`checkAbout_${a}`)}</option>)}
        </select>
      </label>
      {about === 'cell' && (
        <div className="grid gap-2 sm:grid-cols-3">
          <Input aria-label={I18n.get('checkTable')} placeholder={I18n.get('checkTable')} value={table} onChange={(e) => setTable(e.target.value)} />
          <Input aria-label={I18n.get('checkRow')} placeholder={I18n.get('checkRow')} value={row} onChange={(e) => setRow(e.target.value)} />
          <Input aria-label={I18n.get('checkColumn')} placeholder={I18n.get('checkColumn')} value={column} onChange={(e) => setColumn(e.target.value)} />
        </div>
      )}
      {(about === 'field' || about === 'section' || about === 'text') && (
        <Input aria-label={I18n.get(`checkLabel_${about}`)} placeholder={I18n.get(`checkLabel_${about}`)} value={label} onChange={(e) => setLabel(e.target.value)} />
      )}
      {about === 'finding' ? (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_10rem]">
          {findings && findings.length > 0 ? (
            <select aria-label={I18n.get('checkFinding')} value={finding} onChange={(e) => { setFinding(e.target.value); setPasses(findings.find((f) => f.id === e.target.value)?.passed ?? true); }} className={SELECT}>
              {findings.map((f) => <option key={f.id} value={f.id}>{f.id} — {f.message}</option>)}
            </select>
          ) : (
            <Input aria-label={I18n.get('checkFinding')} placeholder={I18n.get('checkFinding')} value={finding} onChange={(e) => setFinding(e.target.value)} />
          )}
          <select aria-label={I18n.get('checkPasses')} value={passes ? 'yes' : 'no'} onChange={(e) => setPasses(e.target.value === 'yes')} className={SELECT}>
            <option value="yes">{I18n.get('expectPasses')}</option>
            <option value="no">{I18n.get('expectFails')}</option>
          </select>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <select aria-label={I18n.get('checkMust')} value={must} onChange={(e) => setMust(e.target.value as Must)} className={SELECT}>
            {MUSTS.filter((m) => about !== 'text' || m === 'present' || m === 'absent').map((m) => <option key={m} value={m}>{I18n.get(`checkMust_${m}`)}</option>)}
          </select>
          {(must === 'reads' || must === 'contains') && <Input aria-label={I18n.get('checkValue')} value={value} onChange={(e) => setValue(e.target.value)} />}
          {must === 'between' && (
            <div className="flex items-center gap-2">
              <Input aria-label={I18n.get('checkLow')} placeholder={I18n.get('checkLow')} inputMode="decimal" value={low} onChange={(e) => setLow(e.target.value)} />
              <span className="text-muted-foreground">–</span>
              <Input aria-label={I18n.get('checkHigh')} placeholder={I18n.get('checkHigh')} inputMode="decimal" value={high} onChange={(e) => setHigh(e.target.value)} />
            </div>
          )}
        </div>
      )}
      {found && found.length > 0 && about !== 'finding' && <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">{I18n.get('checkPrintsNow', { found: found.slice(0, 4).join(' · ') })}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>{I18n.get('cancel')}</Button>
        <Button size="sm" disabled={!check || disabled} onClick={() => check && onAdd(check)}>{addLabel ?? I18n.get('checkAdd')}</Button>
      </div>
    </div>
  );
}

/** How a check came out: held, missed, still being made, or not made yet (never saved). */
export function CheckMark({ result, pending }: { result: CheckResult | undefined; pending?: boolean }) {
  if (pending) return <Loader2 role="img" aria-label={I18n.get('checkPending')} className="size-3.5 shrink-0 animate-spin text-muted-foreground" />;
  if (!result) return <Circle role="img" aria-label={I18n.get('checkNotMade')} className="size-3.5 shrink-0 text-muted-foreground" />;
  return result.ok
    ? <CheckCircle2 role="img" aria-label={I18n.get('designerFixtureHolds')} className="size-3.5 shrink-0 text-emerald-600" />
    : <XCircle role="img" aria-label={I18n.get('designerFixtureFails')} className="size-3.5 shrink-0 text-red-600" />;
}

/** One check in a list: how it came out, what it is about and must be, what it found, and removing it. */
export function CheckRow({ check, result, pending, detail, onRemove }: { check: Check; result?: CheckResult; pending?: boolean; detail?: string; onRemove?: () => void }) {
  return (
    <li className={cn('group/check flex items-start gap-2 px-3 py-2 text-sm', result && !result.ok && 'bg-red-50/60')}>
      <span className="mt-0.5"><CheckMark result={result} pending={pending} /></span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="[overflow-wrap:anywhere]">
          <span className="mr-1.5 rounded-sm bg-muted px-1 text-[0.6rem] font-semibold tracking-wide text-muted-foreground uppercase">{I18n.get(isPrintedCheck(check) ? 'checkKindPrinted' : 'checkKindFinding')}</span>
          {isPrintedCheck(check) && <span className="font-medium">{describeTarget(check.printed)} </span>}
          {describeAssertion(check)}
        </span>
        {result && !result.ok && result.message && <span className="text-xs text-red-700 [overflow-wrap:anywhere]">{result.message}</span>}
        {result?.ok && result.found && result.found.length > 0 && isPrintedCheck(check) && <span className="text-xs text-muted-foreground [overflow-wrap:anywhere]">{I18n.get('checkPrintsNow', { found: result.found.slice(0, 4).join(' · ') })}</span>}
        {detail && <span className="text-xs text-muted-foreground">{detail}</span>}
      </span>
      {onRemove && <IconButton label={I18n.get('checkRemove')} size="icon-xs" className="opacity-60 group-hover/check:opacity-100 hover:text-destructive" onClick={onRemove}><Trash2 /></IconButton>}
    </li>
  );
}

/** Checks that could not be read, and why: until they are fixed in code, the list here is not all the file holds, so it is not edited here. */
export function ChecksUnread({ problems, onOpenCode }: { problems: string[]; onOpenCode: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 border-t-[1px] px-[1rem] py-2 text-xs text-destructive">
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{problems.join(' · ')} — {I18n.get('checksFixInCode')}</span>
      <Button variant="outline" size="xs" onClick={onOpenCode}><Code2 /> {I18n.get('rv2OpenInCode')}</Button>
    </div>
  );
}
